<script lang="ts">
  import { run } from "svelte/legacy";

  import type { FacilityInformation, Person, PersonValidation } from "../../helpers/types";
  import { personValidator } from "../../helpers/validators";
  import Heading from "../atoms/Heading.svelte";
  import NumberInput from "../atoms/NumberInput.svelte";
  import TextInput from "../atoms/TextInput.svelte";
  import type { InformationValidation } from "../../helpers/types";
  import { validateInformation } from "../../helpers/validators";
  import ContactInformation from "./ContactInformation.svelte";
  import ErrorText from "../atoms/ErrorText.svelte";

  interface Props {
    person: Person;
    information: FacilityInformation;
    valid?: boolean;
    useSeperateAddress?: boolean;
    disableInputs?: boolean;
    hasPressedContinue?: boolean;
    hasFetchedPersonAutomatic: boolean;
    clearPersonInfo: () => void;
  }

  let {
    person = $bindable(),
    information = $bindable(),
    valid = $bindable(),
    useSeperateAddress = $bindable(),
    disableInputs = false,
    hasPressedContinue = $bindable(false),
    hasFetchedPersonAutomatic = $bindable(),
    clearPersonInfo,
  }: Props = $props();

  const originalZipcode = information ? information.zipcode : undefined;
  let changedZipcode = $derived(
    originalZipcode && originalZipcode.toString() !== information.zipcode?.toString()
  );

  if (person.address && person.address != information.address) useSeperateAddress = true;

  let personValidation: PersonValidation = $state({ valid: false, errors: {} });
  let informationValidator: InformationValidation = $state({
    valid: false,
    errors: {},
  });

  $effect(() => {
    if (person) {
      personValidation = personValidator(person, !useSeperateAddress);
      valid = person.valid && information.valid;
    }
  });

  $effect(() => {
    if (information) {
      informationValidator = validateInformation(information);
      valid = person.valid && information.valid;
    }
  });
</script>

<div class="text-M">
  {#if hasFetchedPersonAutomatic}
    <button type="button" onclick={clearPersonInfo} class="text-Neutral900 text-sm leading-4 mb-4">
      Stämmer inte uppgifterna nedan? <span class="underline hover:opacity-75"
        >Fyll i uppgifter manuellt</span
      >
    </button>
  {/if}
  <Heading variant={"secondary"}>Dit du vill ha elen</Heading>
  <div class="mb-2 mt-4">
    {#if informationValidator.errors.address || (hasPressedContinue && !information.address)}
      <ErrorText>Fyll i gatuadress</ErrorText>
    {:else}
      <label for="adress" class="block">Gatuadress</label>
    {/if}
    <TextInput
      disabled={disableInputs || hasFetchedPersonAutomatic}
      id="adress"
      name="adress"
      required={hasFetchedPersonAutomatic ? false : true}
      {hasPressedContinue}
      class="w-full"
      error={informationValidator.errors.address || (hasPressedContinue && !information.address)}
      bind:value={information.address}
    />
  </div>
  <div class="mb-4 flex space-x-3">
    <div class="w-32">
      {#if informationValidator.errors.zipcode || (hasPressedContinue && !information.zipcode)}
        <ErrorText>Postnummer</ErrorText>
      {:else}
        <label for="zipcode" class="block">Postnummer</label>
      {/if}
      {#if hasFetchedPersonAutomatic}
        <TextInput
          disabled={disableInputs || hasFetchedPersonAutomatic}
          id="zipcode"
          name="zipcode"
          required={hasFetchedPersonAutomatic ? false : true}
          {hasPressedContinue}
          class="w-28"
          error={informationValidator.errors.zipcode ||
            (hasPressedContinue && !information.zipcode)}
          max={99999}
          bind:value={information.fetchedZipcode}
        />
      {:else}
        <NumberInput
          disabled={disableInputs || hasFetchedPersonAutomatic}
          id="zipcode"
          name="zipcode"
          required={hasFetchedPersonAutomatic ? false : true}
          {hasPressedContinue}
          class="w-28"
          error={informationValidator.errors.zipcode ||
            (hasPressedContinue && !information.zipcode)}
          max={99999}
          bind:value={information.zipcode}
        />
      {/if}
    </div>
    <div class="w-full">
      {#if informationValidator.errors.city || (hasPressedContinue && !information.city)}
        <ErrorText>Fyll i postort</ErrorText>
      {:else}
        <label for="city" class="block">Postort</label>
      {/if}
      <TextInput
        disabled={disableInputs || hasFetchedPersonAutomatic}
        id="city"
        {hasPressedContinue}
        required={hasFetchedPersonAutomatic ? false : true}
        name="city"
        class="w-full"
        error={informationValidator.errors.city || (hasPressedContinue && !information.city)}
        bind:value={information.city}
      />
    </div>
  </div>
  {#if changedZipcode && information.fetchedZipcode}
    <span class="block text-Violet700 text-sm -mt-2"
      >Du har angett ett annat postnummer än tidigare,<br />vilket kan påverka prisuppskattningen du
      fått.</span
    >
  {/if}
</div>
<div class="mt-2">
  <ContactInformation
    bind:hasFetchedPersonAutomatic
    bind:hasPressedContinue
    {disableInputs}
    bind:useSeperateAddress
    bind:person
    {personValidation}
  />
</div>
